import React, { useState, useEffect, useMemo } from "react";
import axios from "../axios";
import { pdf } from "@react-pdf/renderer";
import InvoicePDF from "../components/InvoicePDF_v2";
import { saveAs } from "file-saver";
import DobropisPDF from "../components/DobropisPDF";
import VeleposlanistvoPDF from "../components/VeleposlanistvoPDF";
import PreInvoicePDF from "../components/PreInvoicePDF";
import StornoInvoicePDF from "../components/StornoInvoicePDF";
import { toast } from "react-toastify";
import { addDays, format } from "date-fns";
import { formatEUR, generatePDFName } from "../utils/utils";
import AutomaticInvoiceForm from "./AutomaticInvoiceForm";

const VpisRacuni = () => {
    const [selectedYear, setSelectedYear] = useState("");
    const [availableYears, setAvailableYears] = useState([]);
    const [spreadsheetIds, setSpreadsheetIds] = useState({});
    const [nextInvoiceNumber, setNextInvoiceNumber] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [activeTab, setActiveTab] = useState("automatic");
    const [projects, setProjects] = useState([]);

    // PDF History state
    const [pdfHistory, setPdfHistory] = useState([]);
    const [pdfHistoryLoading, setPdfHistoryLoading] = useState(false);
    const [pdfHistoryError, setPdfHistoryError] = useState(null);
    const [pdfHistoryFilters, setPdfHistoryFilters] = useState({
        year: "",
        month: "",
    });

    // Invoice generator form state
    const [invoiceForm, setInvoiceForm] = useState({
        date: new Date().toISOString().split("T")[0],
        amount: 0,
        company: "",
        invoiceNumber: "",
        description: "",
        language: "SI",
        invoiceType: "invoice",
        companyName: "",
        companyAddress: "",
        companyCity: "",
        companyRegNumber: "",
        companyVAT: "",
        companyEmail: "",
        companyPhone: "",
        propertyAddress: "",
        vatRate: 22,
        paymentDays: 8,
        reference: "SI00",
        discount: 0,
        agent: "",
        project: "",
        billType: "",
        additional_services: 0,
    });
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    // Automatic generator form state
    const [automaticPDFFile, setAutomaticPDFFile] = useState(null);
    const [automaticInvoiceForm, setAutomaticInvoiceForm] = useState(null);
    const [automaticFormSubmitting, setAutomaticFormSubmitting] =
        useState(false);
    const [automaticFormError, setAutomaticFormError] = useState(null);

    // Initialize PDF history filters with selected year when switching to history tab
    useEffect(() => {
        if (
            activeTab === "history" &&
            selectedYear &&
            !pdfHistoryFilters.year
        ) {
            setPdfHistoryFilters((prev) => ({ ...prev, year: selectedYear }));
        }
    }, [activeTab, selectedYear]);

    useEffect(() => {
        if (activeTab === "generator" && selectedYear) {
            const updatedInvoiceNumber =
                invoiceForm.invoiceType === "preinvoice"
                    ? nextInvoiceNumber[selectedYear].preInvoice || ""
                    : invoiceForm.invoiceType === "invoice"
                    ? nextInvoiceNumber[selectedYear].invoice || ""
                    : "";

            setInvoiceForm((prev) => ({
                ...prev,
                invoiceNumber: updatedInvoiceNumber,
            }));
        }
    }, [activeTab, selectedYear, invoiceForm.invoiceType]);

    // Fetch PDF history on component mount and when history tab is selected
    useEffect(() => {
        if (activeTab === "history") {
            fetchPdfHistory(pdfHistoryFilters);
        }
    }, [activeTab, pdfHistoryFilters]);

    // Fetch PDF history from backend
    const fetchPdfHistory = async (filters = {}) => {
        setPdfHistoryLoading(true);
        setPdfHistoryError(null);
        try {
            const params = new URLSearchParams();
            if (filters.year) params.append("year", filters.year);
            if (filters.month) params.append("month", filters.month);

            const response = await axios.get(
                `/api/racuni/pdf-history?${params.toString()}`
            );
            setPdfHistory(response.data.files || []);
        } catch (error) {
            console.error("Error fetching PDF history:", error);
            setPdfHistoryError(
                error.response?.data?.details || "Failed to fetch PDF history"
            );
            toast.error("Failed to fetch PDF history");
        } finally {
            setPdfHistoryLoading(false);
        }
    };

    // Delete a PDF from Google Drive
    const deletePdf = async (fileId, fileName) => {
        if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
            return;
        }

        try {
            await axios.delete(`/api/racuni/pdf/${fileId}`);
            toast.success("PDF deleted successfully");
            // Refresh the list
            fetchPdfHistory();
        } catch (error) {
            console.error("Error deleting PDF:", error);
            toast.error(
                error.response?.data?.details || "Failed to delete PDF"
            );
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "Unknown";
        const date = new Date(dateString);
        return (
            date.toLocaleDateString("sl-SI") +
            " " +
            date.toLocaleTimeString("sl-SI")
        );
    };

    const agents = [
        { label: "Izberite agenta", value: "" },
        { label: "EČ", value: "EČ" },
        { label: "AP", value: "AP" },
        { label: "SĐ", value: "SĐ" },
        { label: "ND", value: "ND" },
        { label: "NMR", value: "NMR" },
        { label: "RL", value: "RL" },
        { label: "JC", value: "JC" },
        { label: "MJ", value: "MJ" },
        { label: "UT", value: "UT" },
        { label: "JD", value: "JD" },
        { label: "TD", value: "TD" },
        { label: "KK", value: "KK" },
        { label: "ZP", value: "ZP" },
        { label: "RLM", value: "RLM" },
        { label: "STO", value: "STO" },
    ];

    const billTypes = [
        { label: "Tip računa", value: "" },
        { label: "Prodaja", value: "prodaja" },
        { label: "Oddaja", value: "oddaja" },
        { label: "Oglaševanje", value: "oglasevanje" },
        { label: "Del dodatnih storitev", value: "del_dodatnih_storitev" },
    ];

    // Calculated invoice values
    const amountWithoutVAT =
        invoiceForm.billType === "oddaja"
            ? parseFloat(invoiceForm.amount) +
                  parseFloat(invoiceForm.additional_services) || 0
            : parseFloat(invoiceForm.amount) || 0;

    const vatAmount = useMemo(() => {
        const discountAmount =
            invoiceForm.discount === 0
                ? 0
                : amountWithoutVAT * (invoiceForm.discount / 100);
        const amountAfterDiscount = amountWithoutVAT - discountAmount;
        return invoiceForm.vatRate === 0
            ? 0
            : amountAfterDiscount * (invoiceForm.vatRate / 100);
    }, [invoiceForm.vatRate, amountWithoutVAT, invoiceForm.discount]);

    const totalWithVAT = useMemo(() => {
        const discountAmount =
            invoiceForm.discount === 0
                ? 0
                : amountWithoutVAT * (invoiceForm.discount / 100);
        const amountAfterDiscount = amountWithoutVAT - discountAmount;
        return amountAfterDiscount + vatAmount;
    }, [amountWithoutVAT, vatAmount, invoiceForm.discount]);

    const discountValues = useMemo(() => {
        const discountAmount =
            invoiceForm.discount === 0
                ? 0
                : amountWithoutVAT * (invoiceForm.discount / 100);
        return {
            discountPercentage: invoiceForm.discount,
            discountAmount: discountAmount,
        };
    }, [amountWithoutVAT, invoiceForm.discount]);

    const generatePdfBlob = async () => {
        const today = new Date();

        // Ensure no time component — normalize to start of the day
        const startOfToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );

        const daysToAdd = Number(invoiceForm.paymentDays || 0);
        const newDate = addDays(startOfToday, daysToAdd);

        const paymentDate = format(newDate, "dd. MM. yyyy");

        let PDFFile = null;

        switch (invoiceForm.invoiceType) {
            case "invoice":
                const invoicePdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyRegNumber: invoiceForm.companyRegNumber,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                    agent: invoiceForm.agent,
                };
                PDFFile = <InvoicePDF data={invoicePdfData} />;
                break;
            case "preinvoice":
                const preInvoicePdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyRegNumber: invoiceForm.companyRegNumber,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                    agent: invoiceForm.agent,
                };
                PDFFile = <PreInvoicePDF data={preInvoicePdfData} />;
                break;
            case "storno_invoice":
                const stornoInvoicePdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.companyName,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyRegNumber: invoiceForm.companyRegNumber,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    companyName: invoiceForm.company,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                    agent: invoiceForm.agent,
                };
                PDFFile = <StornoInvoicePDF data={stornoInvoicePdfData} />;
                break;
            case "dobropis":
                const dobropisPdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.companyName,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyRegNumber: invoiceForm.companyRegNumber,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    companyName: invoiceForm.company,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                    agent: invoiceForm.agent,
                };
                PDFFile = <DobropisPDF data={dobropisPdfData} />;
                break;
            case "veleposlanistvo":
                const veleposlanistvoPdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyRegNumber: invoiceForm.companyRegNumber,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                    agent: invoiceForm.agent,
                };
                PDFFile = <VeleposlanistvoPDF data={veleposlanistvoPdfData} />;
                break;
            default:
                PDFFile = null;
                break;
        }

        if (!PDFFile) {
            throw new Error("Napaka pri ustvarjanju PDF-ja.");
        }

        const blob = await pdf(PDFFile).toBlob();
        return blob;
    };

    const handleDownload = async () => {
        try {
            setDownloadError(null);
            setDownloading(true);

            const blob = await generatePdfBlob();
            saveAs(
                blob,
                `${generatePDFName(
                    invoiceForm.company,
                    invoiceForm.invoiceNumber
                )}.pdf`
            );
        } catch (error) {
            console.error(error);
            setDownloadError("Napaka pri prenosu.");
        } finally {
            setDownloading(false);
        }
    };

    const getSheetUrl = () => {
        if (!selectedYear || !spreadsheetIds[selectedYear])
            return "about:blank";
        return `https://docs.google.com/spreadsheets/d/${spreadsheetIds[selectedYear]}/edit`;
    };

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
        if (!isFullScreen) {
            window.scrollTo(0, 0);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInvoiceForm((prev) => {
            const updatedForm = {
                ...prev,
                [name]: value,
            };

            // Auto-generate reference when date or invoice number changes
            if (name === "date" || name === "invoiceNumber") {
                const invoiceNumber =
                    name === "invoiceNumber" ? value : prev.invoiceNumber;
                const date = name === "date" ? value : prev.date;

                if (name === "invoiceNumber" && value.length === 0) {
                    updatedForm.reference = "SI00";
                } else if (invoiceNumber && date) {
                    const year = new Date(date).getFullYear();
                    updatedForm.reference = `SI00 ${invoiceNumber} ${year}`;
                }
            }

            return updatedForm;
        });
    };

    const handleSubmitInvoice = async (e) => {
        e.preventDefault();

        setFormSubmitting(true);
        setFormError(null);

        try {
            if (
                !invoiceForm.invoiceNumber ||
                !invoiceForm.date ||
                !invoiceForm.company ||
                !invoiceForm.amount ||
                !invoiceForm.agent
            ) {
                setFormError("Prosimo, izpolnite vsa obvezna polja");
                return;
            }

            // Generate PDF blob with compression
            let pdfBlob = null;
            try {
                pdfBlob = await generatePdfBlob();

                // Check blob size (limit to ~10MB for transmission)
                if (pdfBlob.size > 10 * 1024 * 1024) {
                    console.warn("PDF blob too large:", pdfBlob.size, "bytes");
                    setFormError("PDF je prevelik za nalaganje");
                    return;
                }

                // Convert blob to base64 for transmission
                const reader = new FileReader();
                const base64Promise = new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.ontimeout = () =>
                        reject(new Error("Timeout reading PDF"));
                });

                // Add timeout for large files
                setTimeout(() => {
                    if (reader.readyState === FileReader.LOADING) {
                        reader.abort();
                    }
                }, 30000); // 30 second timeout

                reader.readAsDataURL(pdfBlob);
                const base64Data = await base64Promise;

                console.log(
                    "PDF blob generated successfully, size:",
                    pdfBlob.size,
                    "bytes"
                );

                // Add blob to form data
                const formDataWithBlob = {
                    ...invoiceForm,
                    blob: base64Data,
                };

                await axios.post(
                    `/api/racuni/${selectedYear}`,
                    formDataWithBlob,
                    {
                        timeout: 60000, // 60 second timeout for large uploads
                        maxContentLength: 50 * 1024 * 1024, // 50MB limit
                    }
                );
            } catch (pdfError) {
                console.warn("Error generating PDF blob:", pdfError);
                // Show warning but continue without PDF
                toast.warn(
                    "PDF se ni mogel naložiti v oblak, račun je bil kljub temu dodan"
                );
                await axios.post(`/api/racuni/${selectedYear}`, invoiceForm);
            }

            toast.success("Račun uspešno dodan!");
        } catch (error) {
            console.error("Error submitting invoice:", error);

            // Handle specific error types
            if (error.code === "ECONNABORTED") {
                setFormError(
                    "Zahteva je bila prekinjena zaradi časovne omejitve"
                );
            } else if (error.response?.status === 413) {
                setFormError("PDF je prevelik za nalaganje");
            } else if (error.response?.status === 507) {
                setFormError("Ni dovolj prostora za nalaganje PDF-ja");
            } else {
                setFormError(
                    error.response?.data?.error ||
                        error.message ||
                        "Napaka pri dodajanju računa"
                );
            }
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleAutomaticPDFFile = (e) => {
        // e.target.files is a FileList
        const file = e.target.files[0]; // get the first selected file
        setAutomaticPDFFile(file);
    };

    const handleAutomationSubmit = async (e) => {
        e.preventDefault();

        setAutomaticFormSubmitting(true);
        setAutomaticFormError(null);

        try {
            if (!automaticPDFFile) {
                setAutomaticFormError("Prosimo, izberite PDF datoteko");
                return;
            }

            // Generate PDF blob with compression
            const pdfBlob = new Blob([automaticPDFFile], {
                type: automaticPDFFile.type,
            });

            // Check blob size (limit to ~10MB for transmission)
            if (pdfBlob.size > 10 * 1024 * 1024) {
                console.warn("PDF blob too large:", pdfBlob.size, "bytes");
                setAutomaticFormError("PDF je prevelik za nalaganje");
                return;
            }

            // Convert blob to base64 for transmission
            const reader = new FileReader();
            const base64Promise = new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.ontimeout = () =>
                    reject(new Error("Timeout reading PDF"));
            });

            // Add timeout for large files
            setTimeout(() => {
                if (reader.readyState === FileReader.LOADING) {
                    reader.abort();
                }
            }, 30000); // 30 second timeout

            reader.readAsDataURL(pdfBlob);
            const base64Data = await base64Promise;

            console.log(
                "PDF blob generated successfully, size:",
                pdfBlob.size,
                "bytes"
            );

            // Add blob to form data
            const formDataWithBlob = {
                blob: base64Data,
            };

            const response = await axios.post(
                `/api/racuni/automatic`,
                formDataWithBlob,
                {
                    timeout: 60000, // 60 second timeout for large uploads
                    maxContentLength: 50 * 1024 * 1024, // 50MB limit
                }
            );

            // Process each contract and set the reference based on invoiceNumber
            const contractsWithReferences = response.data.contracts.map(
                (contract) => {
                    if (
                        !contract.invoiceNumber ||
                        contract.invoiceNumber.length === 0
                    ) {
                        contract.reference = "SI00";
                    } else if (contract.invoiceNumber && contract.date) {
                        const year = new Date(contract.date).getFullYear();
                        contract.reference = `SI00 ${contract.invoiceNumber} ${year}`;
                    }
                    return contract;
                }
            );

            setAutomaticInvoiceForm(contractsWithReferences);
        } catch (error) {
            console.error("Error submitting invoice:", error);

            // Handle specific error types
            if (error.code === "ECONNABORTED") {
                setAutomaticFormError(
                    "Zahteva je bila prekinjena zaradi časovne omejitve"
                );
            } else if (error.response?.status === 413) {
                setAutomaticFormError("PDF je prevelik za nalaganje");
            } else if (error.response?.status === 507) {
                setAutomaticFormError("Ni dovolj prostora za nalaganje PDF-ja");
            } else {
                setAutomaticFormError(
                    error.response?.data?.error ||
                        error.message ||
                        "Napaka pri dodajanju računa"
                );
            }
        } finally {
            setAutomaticFormSubmitting(false);
        }
    };

    // Render PDF History Tab
    // Group PDFs by month for display
    const groupedPdfHistory = useMemo(() => {
        const groups = {};
        pdfHistory.forEach((file) => {
            // Use invoice date if available, otherwise use creation date
            const date = file.invoiceDate
                ? new Date(file.invoiceDate)
                : new Date(file.createdTime);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const monthName = date.toLocaleDateString("sl-SI", {
                month: "long",
            });

            const key = `${year}-${month.toString().padStart(2, "0")}`;
            if (!groups[key]) {
                groups[key] = {
                    year: year,
                    month: month,
                    monthName: monthName,
                    files: [],
                };
            }
            groups[key].files.push(file);
        });

        // Sort groups by year and month (newest first)
        return Object.entries(groups)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([key, group]) => ({ key, ...group }));
    }, [pdfHistory]);

    const renderFullScreenOverlay = () => {
        if (!isFullScreen) return null;
        return (
            <div
                className="fixed inset-0 z-50 flex flex-col bg-black bg-opacity-80"
                style={{ overflow: "hidden" }}
            >
                <div className="flex items-center justify-between p-4 bg-white bg-opacity-10">
                    <h2 className="font-medium text-white">
                        Računi {selectedYear}
                    </h2>
                    <button
                        className="px-4 py-2 font-medium text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700"
                        onClick={toggleFullScreen}
                    >
                        Izhod iz celozaslonskega načina
                    </button>
                </div>
                <div className="flex items-center justify-center flex-1">
                    <iframe
                        src={getSheetUrl()}
                        className="w-full h-full"
                        frameBorder="0"
                        style={{ background: "white", border: "none" }}
                        title="Računi"
                        allowFullScreen
                        loading="lazy"
                    />
                </div>
            </div>
        );
    };

    const renderSheetsTab = () => (
        <div
            className="overflow-hidden bg-white rounded-lg shadow-md"
            style={{ height: "80vh" }}
        >
            <iframe
                src={getSheetUrl()}
                className="w-full h-full"
                frameBorder="0"
                style={{ border: "none" }}
                title="Računi"
                allowFullScreen
                loading="lazy"
            />
        </div>
    );

    const renderGeneratorTab = () => (
        <div className="p-6 bg-white rounded-lg shadow-lg">
            <h3 className="pb-3 mb-6 text-xl font-medium text-gray-800 border-b">
                Ustvari nov račun
            </h3>

            {formError && (
                <div className="flex items-center p-4 mb-6 text-red-800 bg-red-100 border border-red-200 rounded-md">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                    {formError}
                </div>
            )}

            <form onSubmit={handleSubmitInvoice} className="space-y-8">
                {/* Basic invoice information */}
                <div className="p-5 rounded-lg shadow-sm bg-gray-50">
                    <div className="flex items-center mb-4 text-blue-700">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                        <h4 className="leading-[1.1] text-lg font-medium">
                            Osnovni podatki računa
                        </h4>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Tip dokumenta*
                            </label>
                            <select
                                name="invoiceType"
                                value={invoiceForm.invoiceType}
                                onChange={handleInputChange}
                                className="w-full p-2 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                            >
                                <option value="invoice">Račun</option>
                                <option value="preinvoice">Predračun</option>
                                <option value="dobropis">Dobropis</option>
                                <option value="storno_invoice">
                                    Storno račun
                                </option>
                                <option value="veleposlanistvo">
                                    Veleposlaništvo
                                </option>
                            </select>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Jezik*
                            </label>
                            <select
                                name="language"
                                value={invoiceForm.language}
                                onChange={handleInputChange}
                                className="w-full p-2 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                            >
                                <option value="SI">Slovenski</option>
                                <option value="EN">Angleški</option>
                            </select>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Datum*
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="date"
                                    name="date"
                                    value={invoiceForm.date}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Številka računa*
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="invoiceNumber"
                                    value={invoiceForm.invoiceNumber}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Referenca
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="reference"
                                    value={invoiceForm.reference}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="SI00 številka"
                                    readOnly
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Rok plačila (dni)
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="number"
                                    name="paymentDays"
                                    value={invoiceForm.paymentDays}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    min="0"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Client information */}
                <div className="p-5 rounded-lg shadow-sm bg-gray-50">
                    <div className="flex items-center mb-4 text-blue-700">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                        </svg>
                        <h4 className="leading-[1.1] text-lg font-medium">
                            Podatki o stranki
                        </h4>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Podjetje/Ime*
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="company"
                                    value={invoiceForm.company}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Naslov
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="companyAddress"
                                    value={invoiceForm.companyAddress}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Mesto in poštna številka
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="companyCity"
                                    value={invoiceForm.companyCity}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                ID za DDV / Davčna številka
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="companyVAT"
                                    value={invoiceForm.companyVAT}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                E-naslov
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        className="w-5 h-5 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M16.5 12a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 1 0-2.636 6.364M16.5 12V8.25"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="companyEmail"
                                    value={invoiceForm.companyEmail}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="john@example.com"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Telefon
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        className="w-5 h-5 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="companyPhone"
                                    value={invoiceForm.companyPhone}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="040123123"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Service information */}
                <div className="p-5 rounded-lg shadow-sm bg-gray-50">
                    <div className="flex items-center mb-4 text-blue-700">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                        <h4 className="leading-[1.1] text-lg font-medium">
                            Podatki o storitvi
                        </h4>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Naslov nepremičnine
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="propertyAddress"
                                    value={invoiceForm.propertyAddress}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Tip računa
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="size-6"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M6 6h.008v.008H6V6Z"
                                        />
                                    </svg>
                                </div>
                                <select
                                    name="billType"
                                    value={invoiceForm.billType}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        setInvoiceForm((prev) => ({
                                            ...prev,
                                            additional_services: 0,
                                        }));
                                    }}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {billTypes.map((type) => (
                                        <option
                                            key={type.value}
                                            value={type.value}
                                        >
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Izberite agenta
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="size-6"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                                        />
                                    </svg>
                                </div>
                                <select
                                    name="agent"
                                    value={invoiceForm.agent}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {agents.map((agent) => (
                                        <option
                                            key={agent.value}
                                            value={agent.value}
                                        >
                                            {agent.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {invoiceForm.agent === "STO" && (
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                    Izberite projekt
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            fill="currentColor"
                                            className="bi bi-house-gear"
                                            viewBox="0 0 16 16"
                                        >
                                            <path d="M7.293 1.5a1 1 0 0 1 1.414 0L11 3.793V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v3.293l2.354 2.353a.5.5 0 0 1-.708.708L8 2.207l-5 5V13.5a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 2 13.5V8.207l-.646.647a.5.5 0 1 1-.708-.708z" />
                                            <path d="M11.886 9.46c.18-.613 1.048-.613 1.229 0l.043.148a.64.64 0 0 0 .921.382l.136-.074c.561-.306 1.175.308.87.869l-.075.136a.64.64 0 0 0 .382.92l.149.045c.612.18.612 1.048 0 1.229l-.15.043a.64.64 0 0 0-.38.921l.074.136c.305.561-.309 1.175-.87.87l-.136-.075a.64.64 0 0 0-.92.382l-.045.149c-.18.612-1.048.612-1.229 0l-.043-.15a.64.64 0 0 0-.921-.38l-.136.074c-.561.305-1.175-.309-.87-.87l.075-.136a.64.64 0 0 0-.382-.92l-.148-.044c-.613-.181-.613-1.049 0-1.23l.148-.043a.64.64 0 0 0 .382-.921l-.074-.136c-.306-.561.308-1.175.869-.87l.136.075a.64.64 0 0 0 .92-.382zM14 12.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0" />
                                        </svg>
                                    </div>
                                    <select
                                        name="project"
                                        value={invoiceForm.project}
                                        onChange={handleInputChange}
                                        className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value=""></option>
                                        {projects.map((project, key) => (
                                            <option
                                                key={key}
                                                value={project.value}
                                            >
                                                {project.value}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                {invoiceForm.billType === "oddaja"
                                    ? "Provizija brez DDV (€)*"
                                    : "Znesek brez DDV (€)*"}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="number"
                                    name="amount"
                                    value={invoiceForm.amount}
                                    onChange={handleInputChange}
                                    step="0.01"
                                    min="0"
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        {invoiceForm.billType === "oddaja" && (
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                    Del dodatnih storitev
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5 text-gray-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <input
                                        type="number"
                                        name="additional_services"
                                        value={invoiceForm.additional_services}
                                        onChange={handleInputChange}
                                        step="0.01"
                                        min="0"
                                        className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Stopnja DDV (%)
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                        />
                                    </svg>
                                </div>
                                <select
                                    name="vatRate"
                                    value={invoiceForm.vatRate}
                                    onChange={handleInputChange}
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="0">0%</option>
                                    <option value="9.5">9.5%</option>
                                    <option value="22">22%</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Popust
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    name="discount"
                                    value={`${invoiceForm.discount}%`}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(
                                            "%",
                                            ""
                                        );
                                        const numeric = raw.replace(
                                            /[^\d.]/g,
                                            ""
                                        );

                                        // Convert to number and clamp between 0 and 100
                                        const clamped = Math.max(
                                            0,
                                            Math.min(
                                                100,
                                                parseFloat(numeric || 0)
                                            )
                                        );
                                        setInvoiceForm({
                                            ...invoiceForm,
                                            discount: clamped,
                                        });
                                    }}
                                    className="w-full h-[38px] p-2 pl-10 transition-colors bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Opis storitve
                            </label>
                            <div className="relative">
                                <div className="absolute flex items-start pointer-events-none top-3 left-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 6h16M4 12h16M4 18h7"
                                        />
                                    </svg>
                                </div>
                                <textarea
                                    name="description"
                                    value={invoiceForm.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Invoice Summary */}
                <div className="p-5 border border-blue-100 rounded-lg shadow-sm bg-gradient-to-r from-blue-50 to-gray-50">
                    <div className="flex items-center mb-4 text-blue-700">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                        </svg>
                        <h4 className="leading-[1.1] text-lg font-medium">
                            Povzetek računa
                        </h4>
                    </div>

                    <div className="p-5 bg-white border border-gray-200 rounded-md shadow-inner">
                        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                            <span className="text-sm text-gray-700">
                                Vrednost brez DDV:
                            </span>
                            <span className="text-base font-medium sm:text-lg min-w-[85px] text-right">
                                {formatEUR(amountWithoutVAT)} €
                            </span>
                        </div>
                        {discountValues.discountPercentage > 0 &&
                            discountValues.discountAmount > 0 && (
                                <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                                    <span className="text-sm text-gray-700">
                                        Popust (
                                        {discountValues.discountPercentage}%):
                                    </span>
                                    <span className="text-base font-medium sm:text-lg min-w-[85px] text-right">
                                        {formatEUR(
                                            discountValues.discountAmount
                                        )}{" "}
                                        €
                                    </span>
                                </div>
                            )}
                        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                            <span className="text-sm text-gray-700">
                                DDV ({invoiceForm.vatRate}%):
                            </span>
                            <span className="text-base font-medium sm:text-lg min-w-[85px] text-right">
                                {formatEUR(vatAmount)} €
                            </span>
                        </div>
                        <div className="flex flex-col items-center justify-between pt-1 sm:flex-row">
                            <span className="font-semibold text-gray-800">
                                Skupaj za plačilo:
                            </span>
                            <span className="text-xl font-bold text-blue-700">
                                {formatEUR(totalWithVAT)} €
                            </span>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col items-center justify-between gap-4 pt-4 sm:flex-row">
                    <button
                        type="button"
                        onClick={handleDownload}
                        className={`flex items-center justify-center w-full px-6 py-3 font-medium text-white transition-colors rounded-lg shadow-md sm:w-auto hover:shadow-lg ${
                            downloadError
                                ? "bg-red-500 hover:bg-red-600"
                                : "bg-gray-600 hover:bg-gray-700"
                        }`}
                    >
                        {!downloadError && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        )}
                        {downloadError
                            ? "Napaka, poskusite znova"
                            : downloading
                            ? "Pripravljam račun..."
                            : "Prenesi račun"}
                    </button>

                    {invoiceForm.invoiceType !== "storno_invoice" &&
                        invoiceForm.invoiceType !== "dobropis" && (
                            <div className="flex flex-col w-full gap-4 sm:flex-row sm:w-auto">
                                <button
                                    type="submit"
                                    disabled={formSubmitting}
                                    className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium text-white transition-colors flex items-center justify-center shadow-md hover:shadow-lg ${
                                        formSubmitting
                                            ? "bg-gray-400 cursor-not-allowed"
                                            : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 mr-2"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                        />
                                    </svg>
                                    {formSubmitting
                                        ? "Dodajanje..."
                                        : "Dodaj račun"}
                                </button>
                            </div>
                        )}
                </div>
            </form>
        </div>
    );

    function renderPdfHistoryTab() {
        return (
            <div className="p-6 bg-white rounded-lg shadow">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">
                        Zgodovina izdanih računov
                    </h2>
                    <button
                        onClick={() => fetchPdfHistory(pdfHistoryFilters)}
                        className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        disabled={pdfHistoryLoading}
                    >
                        {pdfHistoryLoading ? "Osveževanje..." : "Osveži"}
                    </button>
                </div>

                {/* Date Filters */}
                <div className="p-4 mb-6 rounded-lg bg-gray-50">
                    <h3 className="mb-3 text-sm font-medium text-gray-700">
                        Filtri
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Leto
                            </label>
                            <select
                                value={pdfHistoryFilters.year}
                                onChange={(e) =>
                                    setPdfHistoryFilters((prev) => ({
                                        ...prev,
                                        year: e.target.value,
                                    }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Vsa leta</option>
                                {availableYears.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Mesec
                            </label>
                            <select
                                value={pdfHistoryFilters.month}
                                onChange={(e) =>
                                    setPdfHistoryFilters((prev) => ({
                                        ...prev,
                                        month: e.target.value,
                                    }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Vsi meseci</option>
                                <option value="01">Januar</option>
                                <option value="02">Februar</option>
                                <option value="03">Marec</option>
                                <option value="04">April</option>
                                <option value="05">Maj</option>
                                <option value="06">Junij</option>
                                <option value="07">Julij</option>
                                <option value="08">Avgust</option>
                                <option value="09">September</option>
                                <option value="10">Oktober</option>
                                <option value="11">November</option>
                                <option value="12">December</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() =>
                                    setPdfHistoryFilters({
                                        year: "",
                                        month: "",
                                    })
                                }
                                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Počisti filtre
                            </button>
                        </div>
                    </div>
                </div>

                {pdfHistoryError && (
                    <div className="p-4 mb-4 border border-red-200 rounded-lg bg-red-50">
                        <p className="text-red-600">{pdfHistoryError}</p>
                    </div>
                )}

                {pdfHistoryLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                        <span className="ml-3 text-gray-600">
                            Nalaganje PDF datotek
                        </span>
                    </div>
                ) : pdfHistory.length === 0 ? (
                    <div className="py-8 text-center">
                        <p className="text-gray-500">
                            V zgodovini ni bilo najdenih PDF datotek.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {groupedPdfHistory.map((group) => (
                            <div
                                key={group.key}
                                className="overflow-hidden border border-gray-200 rounded-lg"
                            >
                                <div className="px-6 py-3 bg-gray-100 border-b border-gray-200">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {group.monthName} {group.year}
                                        <span className="ml-2 text-sm text-gray-600">
                                            ({group.files.length} računov)
                                        </span>
                                    </h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Ime datoteke
                                                </th>
                                                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Datum računa
                                                </th>
                                                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Podjetje
                                                </th>
                                                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Znesek
                                                </th>
                                                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                    Velikost
                                                </th>
                                                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {group.files.map((file) => (
                                                <tr
                                                    key={file.id}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {file.name}
                                                            </div>
                                                            {file.invoiceType &&
                                                                file.invoiceType !==
                                                                    "invoice" && (
                                                                    <span
                                                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                                            file.invoiceType ===
                                                                            "storno_invoice"
                                                                                ? "bg-red-100 text-red-800"
                                                                                : file.invoiceType ===
                                                                                  "dobropisy"
                                                                                ? "bg-green-100 text-green-800"
                                                                                : "bg-gray-100 text-gray-800"
                                                                        }`}
                                                                    >
                                                                        {file.invoiceType ===
                                                                        "storno_invoice"
                                                                            ? "Storno"
                                                                            : file.invoiceType ===
                                                                              "dobropisy"
                                                                            ? "Dobropisi"
                                                                            : file.invoiceType}
                                                                    </span>
                                                                )}
                                                        </div>
                                                        {file.invoiceNumber && (
                                                            <div className="text-xs text-gray-500">
                                                                #
                                                                {
                                                                    file.invoiceNumber
                                                                }
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {file.invoiceDate
                                                                ? formatDate(
                                                                      file.invoiceDate
                                                                  )
                                                                : formatDate(
                                                                      file.createdTime
                                                                  )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {file.company ||
                                                                "-"}
                                                        </div>
                                                        {file.agent && (
                                                            <div className="text-xs text-gray-500">
                                                                Agent:{" "}
                                                                {file.agent}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {file.amount
                                                                ? `€${file.amount}`
                                                                : "-"}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {formatFileSize(
                                                                file.size
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                                                        <div className="flex space-x-2">
                                                            <a
                                                                href={
                                                                    file.webViewLink
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-900"
                                                            >
                                                                Poglej
                                                            </a>
                                                            <a
                                                                href={
                                                                    file.webContentLink
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-green-600 hover:text-green-900"
                                                            >
                                                                Prenesi
                                                            </a>
                                                            <button
                                                                onClick={() =>
                                                                    deletePdf(
                                                                        file.id,
                                                                        file.name
                                                                    )
                                                                }
                                                                className="text-red-600 hover:text-red-900"
                                                            >
                                                                Izbriši
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    function renderAutomaticTab() {
        return (
            <div className="p-6 bg-white rounded-lg shadow-lg">
                <div className="flex flex-col items-start justify-between gap-2 pb-3 mb-6 border-b md:items-center md:flex-row">
                    <h3 className="text-xl font-medium text-gray-800 ">
                        Avtomatska izdaja računa
                    </h3>
                    <button
                        className="flex items-center justify-center px-4 py-2 font-medium text-white transition-colors bg-blue-600 rounded-lg shadow-sm md:mb-0 hover:shadow-md hover:bg-blue-700"
                        onClick={() => {
                            setAutomaticFormError(null);
                            setAutomaticPDFFile(null);
                            setAutomaticInvoiceForm(null);
                            setAutomaticFormSubmitting(false);
                        }}
                    >
                        Ponastavi
                    </button>
                </div>

                {automaticFormError && (
                    <div className="flex items-center p-4 mb-6 text-red-800 bg-red-100 border border-red-200 rounded-md">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        {automaticFormError}
                    </div>
                )}

                <form onSubmit={handleAutomationSubmit} className="space-y-8">
                    {/* Basic invoice information */}
                    <div className="p-5 rounded-lg shadow-sm bg-gray-50">
                        <div className="flex items-center mb-4 text-blue-700">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            <h4 className="leading-[1.1] text-lg font-medium">
                                Pogodba
                            </h4>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                    Izberite PDF pogodbo:
                                </label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        id="automatic_pdf_file_upload"
                                        name="automatic_pdf_file_upload"
                                        onChange={handleAutomaticPDFFile}
                                        className="hidden"
                                        accept=".pdf"
                                        required
                                    />
                                    <label
                                        htmlFor="automatic_pdf_file_upload"
                                        className="flex items-center justify-center w-full p-3 transition-colors border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-blue-500 hover:bg-blue-50"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5 mr-2 text-gray-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                            />
                                        </svg>
                                        <span className="text-sm text-gray-600">
                                            {automaticPDFFile
                                                ? automaticPDFFile.name
                                                : "Kliknite za nalaganje PDF"}
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {!automaticInvoiceForm && (
                        <>
                            {/* Action Buttons */}
                            <div className="flex flex-col items-center justify-between gap-4 pt-4 sm:flex-row">
                                <div className="flex flex-col w-full gap-4 sm:flex-row sm:w-auto">
                                    <button
                                        type="submit"
                                        disabled={
                                            automaticFormSubmitting ||
                                            !automaticPDFFile
                                        }
                                        className={`w-full sm:w-auto px-6  py-3 rounded-lg font-medium text-white transition-colors flex items-center justify-center shadow-md hover:shadow-lg ${
                                            automaticFormSubmitting ||
                                            !automaticPDFFile
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-blue-600 hover:bg-blue-700"
                                        }`}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5 mr-2"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                            />
                                        </svg>
                                        {automaticFormSubmitting
                                            ? "Dodajanje..."
                                            : "Dodaj"}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </form>

                {automaticInvoiceForm && automaticInvoiceForm.length > 0 && (
                    <AutomaticInvoiceForm
                        resetAutomationForm={() => {
                            setAutomaticFormError(null);
                            setAutomaticPDFFile(null);
                            setAutomaticInvoiceForm(null);
                            setAutomaticFormSubmitting(false);
                        }}
                        initialData={automaticInvoiceForm}
                        selectedYear={selectedYear}
                        agents={agents}
                        billTypes={billTypes}
                        projects={projects}
                        amountWithoutVAT={amountWithoutVAT}
                        discountValues={discountValues}
                        vatAmount={vatAmount}
                        totalWithVAT={totalWithVAT}
                    />
                )}
            </div>
        );
    }

    useEffect(() => {
        const fetchYears = async () => {
            try {
                const response = await axios.get("/api/racuni/years");
                const years = response.data?.years || [];
                const ids = response.data?.spreadsheetIds || {};
                const nextInvoiceNumberRsp =
                    response.data?.nextInvoiceNumber || null;

                setAvailableYears(years);
                setSpreadsheetIds(ids);
                setNextInvoiceNumber(nextInvoiceNumberRsp);

                if (years.length > 0) {
                    setSelectedYear(years[0]);
                }
            } catch (error) {
                console.error("Error fetching years:", error);
                setError("Napaka pri pridobivanju let");
                setAvailableYears([]);
            } finally {
                setLoading(false);
            }
        };

        fetchYears();
    }, []);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.get("/api/racuni/projects");
                const projects = response.data?.projects || [];

                setProjects(projects);
            } catch (error) {
                console.error("Error fetching projects:", error);
                setError("Napaka pri pridobivanju projektov");
                setAvailableYears([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    if (loading) {
        return <div className="p-4">Nalaganje...</div>;
    }

    if (error) {
        return <div className="p-4 text-red-500">{error}</div>;
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                    <h2 className="text-xl font-semibold">Računi</h2>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        className="p-2 border rounded"
                    >
                        <option value="">Izberi leto</option>
                        {Array.isArray(availableYears) &&
                            availableYears.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                    </select>
                </div>

                {activeTab === "sheets" && (
                    <button
                        onClick={toggleFullScreen}
                        className="px-4 py-2 font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                        Celozaslonski način
                    </button>
                )}
            </div>

            <div className="mb-4 overflow-hidden border-b border-gray-200">
                <nav className="flex flex-row -mb-px overflow-x-auto flex-nowrap">
                    <button
                        onClick={() => setActiveTab("automatic")}
                        className={`ml-8 py-2 px-4 font-medium text-sm ${
                            activeTab === "automatic"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                        Avtomatska izdaja računa
                    </button>
                    <button
                        onClick={() => setActiveTab("generator")}
                        className={`py-2 px-4 font-medium text-sm ${
                            activeTab === "generator"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                        Ročna izdaja računa
                    </button>
                    <button
                        onClick={() => setActiveTab("sheets")}
                        className={`ml-8 py-2 px-4 font-medium text-sm ${
                            activeTab === "sheets"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                        Pregled računov
                    </button>
                    <button
                        onClick={() => setActiveTab("history")}
                        className={`ml-8 py-2 px-4 font-medium text-sm ${
                            activeTab === "history"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                        Zgodovina izdanih računov
                    </button>
                </nav>
            </div>

            {activeTab === "generator" && renderGeneratorTab()}
            {activeTab === "sheets" && renderSheetsTab()}
            {activeTab === "history" && renderPdfHistoryTab()}
            {activeTab === "automatic" && renderAutomaticTab()}

            {renderFullScreenOverlay()}
        </div>
    );
};

export default VpisRacuni;
